/**
 * Figma-style box-model widget for the spacing controls panel.
 *
 * Renders an SVG diagram showing margin (outer rectangle) and padding (inner
 * rectangle) with each side as a click/scrub target. Side labels show the
 * current value and double as scrubber handles. Inputs become editable on
 * focus or double-click. Supports per-pair link toggles plus a center
 * "lock all 4" affordance for both padding and margin.
 *
 * The widget owns no state of its own — the panel passes values in via
 * setValues({ field: cssValue }) and the widget emits semantic events
 * (focus-side, scrub-start, edit-commit, link-toggle, reset-side).
 */

import { escapeHTML as escapeHtml } from '../shared/html-escape.js';

const FIELDS = Object.freeze([
  'paddingTop',
  'paddingRight',
  'paddingBottom',
  'paddingLeft',
  'marginTop',
  'marginRight',
  'marginBottom',
  'marginLeft'
]);

const SIDE_AXIS = Object.freeze({
  paddingTop: 'y',
  paddingBottom: 'y',
  marginTop: 'y',
  marginBottom: 'y',
  paddingLeft: 'x',
  paddingRight: 'x',
  marginLeft: 'x',
  marginRight: 'x'
});

const SIDE_GROWTH_SIGN = Object.freeze({
  paddingTop: 1,
  paddingBottom: 1,
  paddingLeft: 1,
  paddingRight: 1,
  marginTop: -1,
  marginBottom: 1,
  marginLeft: -1,
  marginRight: 1
});

const FIELD_KIND = Object.freeze({
  paddingTop: 'padding',
  paddingRight: 'padding',
  paddingBottom: 'padding',
  paddingLeft: 'padding',
  marginTop: 'margin',
  marginRight: 'margin',
  marginBottom: 'margin',
  marginLeft: 'margin'
});

const PAIR_GROUPS = Object.freeze({
  paddingY: ['paddingTop', 'paddingBottom'],
  paddingX: ['paddingLeft', 'paddingRight'],
  marginY: ['marginTop', 'marginBottom'],
  marginX: ['marginLeft', 'marginRight']
});

const LINK_PAIRS = Object.freeze([
  {
    id: 'paddingY',
    side: 'top',
    kind: 'padding',
    axis: 'y',
    fields: ['paddingTop', 'paddingBottom']
  },
  {
    id: 'paddingX',
    side: 'right',
    kind: 'padding',
    axis: 'x',
    fields: ['paddingLeft', 'paddingRight']
  },
  { id: 'marginY', side: 'top', kind: 'margin', axis: 'y', fields: ['marginTop', 'marginBottom'] },
  { id: 'marginX', side: 'right', kind: 'margin', axis: 'x', fields: ['marginLeft', 'marginRight'] }
]);

function ns(tag, attrs = {}) {
  const el = document.createElementNS('http://www.w3.org/2000/svg', tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v == null) continue;
    el.setAttribute(k, String(v));
  }
  return el;
}

function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

export class BoxModelWidget {
  constructor({ container, onEvent }) {
    this._container = container;
    this._onEvent = typeof onEvent === 'function' ? onEvent : () => {};
    this._values = {};
    this._mixed = {};
    this._diversity = {};
    this._auto = {};
    this._template = {};
    this._negative = {};
    this._unit = 'rem';
    this._rootPx = 16;
    this._focusedField = null;
    this._editingField = null;
    this._snapPulseTimers = new Map();
    this._activeInputEl = null;
    this._activeInputCommit = null;
    this._locks = {
      padding: { all: false, x: false, y: false },
      margin: { all: false, x: false, y: false }
    };
    this._sideEls = new Map();
    this._labelEls = new Map();
    this._build();
  }

  destroy() {
    for (const t of this._snapPulseTimers.values()) clearTimeout(t);
    this._snapPulseTimers.clear();
    this._closeActiveInput({ commit: false });
    this._root?.remove();
    this._root = null;
  }

  get root() {
    return this._root;
  }

  setUnit(unit) {
    this._unit = unit === 'px' ? 'px' : 'rem';
    this._renderLabels();
  }

  setRootPx(rootPx) {
    this._rootPx = Number.isFinite(rootPx) && rootPx > 0 ? rootPx : 16;
    this._renderLabels();
  }

  setLocks(locks) {
    if (!locks) return;
    if (locks.padding) Object.assign(this._locks.padding, locks.padding);
    if (locks.margin) Object.assign(this._locks.margin, locks.margin);
    this._renderLockButtons();
  }

  getLocks() {
    return {
      padding: { ...this._locks.padding },
      margin: { ...this._locks.margin }
    };
  }

  /**
   * Update displayed values.
   *
   * @param {Object} state
   * @param {Object<string,string>} state.values     - field → CSS string (e.g. "1rem", "16px", "auto")
   * @param {Object<string,boolean>} [state.mixed]   - field → mixed flag
   * @param {Object<string,string[]>} [state.diversity] - field → distinct value samples
   * @param {Object<string,boolean>} [state.auto]    - field → has auto value
   * @param {Object<string,boolean>} [state.template] - field → using template/clamp expression
   * @param {Object<string,boolean>} [state.negative] - field → negative value
   */
  setValues(state = {}) {
    if (state.values) this._values = { ...state.values };
    if (state.mixed) this._mixed = { ...state.mixed };
    if (state.diversity) this._diversity = { ...state.diversity };
    if (state.auto) this._auto = { ...state.auto };
    if (state.template) this._template = { ...state.template };
    if (state.negative) this._negative = { ...state.negative };
    this._renderLabels();
  }

  pulseSnap(field) {
    const labelEl = this._labelEls.get(field);
    if (!labelEl) return;
    labelEl.classList.remove('pb-bmw-snap-pulse');
    void labelEl.offsetWidth; // restart animation
    labelEl.classList.add('pb-bmw-snap-pulse');
    if (this._snapPulseTimers.has(field)) clearTimeout(this._snapPulseTimers.get(field));
    const t = setTimeout(() => {
      labelEl.classList.remove('pb-bmw-snap-pulse');
      this._snapPulseTimers.delete(field);
    }, 220);
    this._snapPulseTimers.set(field, t);
  }

  flashClampReject(field) {
    const labelEl = this._labelEls.get(field);
    if (!labelEl) return;
    labelEl.classList.remove('pb-bmw-clamp-reject');
    void labelEl.offsetWidth;
    labelEl.classList.add('pb-bmw-clamp-reject');
    setTimeout(() => labelEl.classList.remove('pb-bmw-clamp-reject'), 320);
  }

  focusField(field) {
    if (!FIELDS.includes(field)) return;
    this._focusedField = field;
    this._renderFocus();
  }

  clearFocus() {
    this._focusedField = null;
    this._renderFocus();
  }

  beginEdit(field) {
    if (!FIELDS.includes(field)) return;
    this._openInputForField(field);
  }

  _emit(type, detail) {
    this._onEvent({ type, ...detail });
  }

  _build() {
    this._root = el('div', 'pb-bmw');
    this._root.tabIndex = 0;
    this._root.setAttribute('role', 'group');
    this._root.setAttribute('aria-label', 'Spacing — margin and padding controls');

    const svg = ns('svg', {
      class: 'pb-bmw-svg',
      viewBox: '0 0 280 180',
      preserveAspectRatio: 'xMidYMid meet'
    });

    const marginRect = ns('rect', {
      class: 'pb-bmw-rect pb-bmw-rect-margin',
      x: 4,
      y: 4,
      width: 272,
      height: 172,
      rx: 6,
      ry: 6
    });
    const paddingRect = ns('rect', {
      class: 'pb-bmw-rect pb-bmw-rect-padding',
      x: 56,
      y: 36,
      width: 168,
      height: 108,
      rx: 4,
      ry: 4
    });
    const innerRect = ns('rect', {
      class: 'pb-bmw-rect pb-bmw-rect-inner',
      x: 96,
      y: 64,
      width: 88,
      height: 52,
      rx: 3,
      ry: 3
    });

    svg.append(marginRect, paddingRect, innerRect);

    // Side hit zones (one per field) — invisible polygons that capture pointer
    // events for scrubbing. Each is paired with a label group.
    this._sideHitZones = {
      marginTop: ns('rect', {
        x: 4,
        y: 4,
        width: 272,
        height: 32,
        class: 'pb-bmw-zone',
        'data-field': 'marginTop'
      }),
      marginBottom: ns('rect', {
        x: 4,
        y: 144,
        width: 272,
        height: 32,
        class: 'pb-bmw-zone',
        'data-field': 'marginBottom'
      }),
      marginLeft: ns('rect', {
        x: 4,
        y: 36,
        width: 52,
        height: 108,
        class: 'pb-bmw-zone',
        'data-field': 'marginLeft'
      }),
      marginRight: ns('rect', {
        x: 224,
        y: 36,
        width: 52,
        height: 108,
        class: 'pb-bmw-zone',
        'data-field': 'marginRight'
      }),
      paddingTop: ns('rect', {
        x: 56,
        y: 36,
        width: 168,
        height: 28,
        class: 'pb-bmw-zone',
        'data-field': 'paddingTop'
      }),
      paddingBottom: ns('rect', {
        x: 56,
        y: 116,
        width: 168,
        height: 28,
        class: 'pb-bmw-zone',
        'data-field': 'paddingBottom'
      }),
      paddingLeft: ns('rect', {
        x: 56,
        y: 64,
        width: 40,
        height: 52,
        class: 'pb-bmw-zone',
        'data-field': 'paddingLeft'
      }),
      paddingRight: ns('rect', {
        x: 184,
        y: 64,
        width: 40,
        height: 52,
        class: 'pb-bmw-zone',
        'data-field': 'paddingRight'
      })
    };
    for (const [field, zone] of Object.entries(this._sideHitZones)) {
      svg.appendChild(zone);
      this._sideEls.set(field, zone);
      zone.addEventListener('pointerdown', (e) => this._onSidePointerDown(e, field));
      zone.addEventListener('dblclick', (e) => {
        e.preventDefault();
        this._openInputForField(field);
      });
      zone.addEventListener('mouseenter', () => this._emit('side-hover', { field }));
      zone.addEventListener('mouseleave', () => this._emit('side-hover', { field: null }));
    }

    // Region labels ("padding" / "margin") for orientation.
    const marginCaption = ns('text', { x: 12, y: 18, class: 'pb-bmw-caption' });
    marginCaption.textContent = 'margin';
    const paddingCaption = ns('text', {
      x: 64,
      y: 50,
      class: 'pb-bmw-caption pb-bmw-caption-padding'
    });
    paddingCaption.textContent = 'padding';
    svg.append(marginCaption, paddingCaption);

    // Value labels (foreignObject so we can use HTML for editing).
    this._labelLayer = ns('g', { class: 'pb-bmw-labels' });
    svg.appendChild(this._labelLayer);
    for (const field of FIELDS) {
      const label = this._buildLabel(field);
      this._labelEls.set(field, label.element);
      this._labelLayer.appendChild(label.foreign);
    }

    this._root.appendChild(svg);

    // Lock affordances: 4 pair-links + 2 center "lock all 4" buttons.
    this._lockLayer = el('div', 'pb-bmw-locks');
    this._lockButtons = new Map();
    for (const pair of LINK_PAIRS) {
      const btn = el('button', `pb-bmw-lock pb-bmw-lock-pair pb-bmw-lock-${pair.id}`, '');
      btn.type = 'button';
      btn.setAttribute('aria-label', `Link ${pair.kind} ${pair.axis}-axis sides`);
      btn.dataset.tooltip = `Link ${pair.kind} ${pair.axis === 'y' ? 'top + bottom' : 'left + right'}`;
      btn.dataset.lockPair = pair.id;
      btn.innerHTML = chainSvg();
      btn.addEventListener('click', () => this._onPairLockToggle(pair));
      this._lockButtons.set(pair.id, btn);
      this._lockLayer.appendChild(btn);
    }
    for (const kind of ['padding', 'margin']) {
      const btn = el('button', `pb-bmw-lock pb-bmw-lock-all pb-bmw-lock-all-${kind}`, '');
      btn.type = 'button';
      btn.setAttribute('aria-label', `Link all four ${kind} sides`);
      btn.dataset.tooltip = `Link all four ${kind} sides`;
      btn.dataset.lockAll = kind;
      btn.innerHTML = chainSvg(true);
      btn.addEventListener('click', () => this._onAllLockToggle(kind));
      this._lockButtons.set(`all-${kind}`, btn);
      this._lockLayer.appendChild(btn);
    }
    this._root.appendChild(this._lockLayer);

    this._root.addEventListener('keydown', (e) => this._onKeyDown(e));

    this._renderLabels();
    this._renderLockButtons();
    this._container.appendChild(this._root);
  }

  _buildLabel(field) {
    const positions = {
      marginTop: { x: 100, y: 4, width: 80, height: 28 },
      marginBottom: { x: 100, y: 148, width: 80, height: 28 },
      marginLeft: { x: 4, y: 78, width: 48, height: 24 },
      marginRight: { x: 228, y: 78, width: 48, height: 24 },
      paddingTop: { x: 100, y: 36, width: 80, height: 24 },
      paddingBottom: { x: 100, y: 120, width: 80, height: 24 },
      paddingLeft: { x: 56, y: 78, width: 40, height: 24 },
      paddingRight: { x: 184, y: 78, width: 40, height: 24 }
    };
    const pos = positions[field];
    const foreign = ns('foreignObject', {
      x: pos.x,
      y: pos.y,
      width: pos.width,
      height: pos.height,
      class: `pb-bmw-label-fo pb-bmw-label-${field}`
    });
    const wrapper = document.createElement('div');
    wrapper.className = `pb-bmw-label pb-bmw-label-${field} pb-bmw-label-${FIELD_KIND[field]}`;
    wrapper.dataset.field = field;
    wrapper.tabIndex = 0;
    wrapper.setAttribute('role', 'spinbutton');
    wrapper.setAttribute(
      'aria-label',
      `${FIELD_KIND[field]} ${field.replace(/^[a-z]+/, '').toLowerCase()}`
    );
    foreign.appendChild(wrapper);

    wrapper.addEventListener('pointerdown', (e) => {
      // Allow the side-zone scrubber to handle the drag, but support direct
      // click-and-drag on the label too.
      if (e.button !== 0) return;
      e.stopPropagation();
      this._onSidePointerDown(e, field);
    });
    wrapper.addEventListener('dblclick', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this._openInputForField(field);
    });
    wrapper.addEventListener('focus', () => {
      this._focusedField = field;
      this._renderFocus();
      this._emit('side-focus', { field });
    });
    wrapper.addEventListener('blur', () => {
      if (this._activeInputEl) return;
      this._renderFocus();
    });

    return { element: wrapper, foreign };
  }

  _onSidePointerDown(e, field) {
    if (e.button !== 0) return;
    // Mouse only. preventDefault on a pointerdown suppresses the compatibility
    // click a tap needs, and the scrub below deliberately does NOT arm a finger
    // until the long press — a finger that travels before then is handed back
    // to the scroller, which cannot happen if the press was already defaulted.
    if (!e.pointerType || e.pointerType === 'mouse') e.preventDefault();
    e.stopPropagation();
    this._closeActiveInput({ commit: true });
    this._focusedField = field;
    this._renderFocus();
    this._emit('scrub-start', {
      field,
      axis: SIDE_AXIS[field],
      sign: SIDE_GROWTH_SIGN[field],
      event: e,
      lockedFields: this._lockedFieldsFor(field)
    });
  }

  _onPairLockToggle(pair) {
    const kindLocks = this._locks[pair.kind];
    const axisKey = pair.axis === 'x' ? 'x' : 'y';
    kindLocks[axisKey] = !kindLocks[axisKey];
    if (kindLocks.x && kindLocks.y) kindLocks.all = false;
    this._renderLockButtons();
    this._emit('link-toggle', {
      kind: pair.kind,
      mode: 'pair',
      axis: axisKey,
      locked: kindLocks[axisKey],
      locks: this.getLocks()
    });
  }

  _onAllLockToggle(kind) {
    const k = this._locks[kind];
    k.all = !k.all;
    if (k.all) {
      k.x = false;
      k.y = false;
    }
    this._renderLockButtons();
    this._emit('link-toggle', { kind, mode: 'all', locked: k.all, locks: this.getLocks() });
  }

  _renderLockButtons() {
    for (const pair of LINK_PAIRS) {
      const btn = this._lockButtons.get(pair.id);
      if (!btn) continue;
      const k = this._locks[pair.kind];
      const active = k.all || k[pair.axis === 'x' ? 'x' : 'y'];
      btn.classList.toggle('pb-bmw-lock-active', !!active);
      btn.classList.toggle('pb-bmw-lock-inherited', !!k.all);
      btn.setAttribute('aria-pressed', String(!!active));
    }
    for (const kind of ['padding', 'margin']) {
      const btn = this._lockButtons.get(`all-${kind}`);
      if (!btn) continue;
      btn.classList.toggle('pb-bmw-lock-active', !!this._locks[kind].all);
      btn.setAttribute('aria-pressed', String(!!this._locks[kind].all));
    }
  }

  _lockedFieldsFor(field) {
    const kind = FIELD_KIND[field];
    const k = this._locks[kind];
    if (k.all) return ['Top', 'Right', 'Bottom', 'Left'].map((s) => `${kind}${s}`);
    const axis = SIDE_AXIS[field];
    if (axis === 'y' && k.y) return PAIR_GROUPS[`${kind}Y`];
    if (axis === 'x' && k.x) return PAIR_GROUPS[`${kind}X`];
    return [field];
  }

  _renderLabels() {
    for (const field of FIELDS) {
      const wrapper = this._labelEls.get(field);
      if (!wrapper) continue;
      if (this._editingField === field) continue;

      const mixed = !!this._mixed[field];
      const auto = !!this._auto[field];
      const template = !!this._template[field];
      const negative = !!this._negative[field];

      wrapper.classList.toggle('pb-bmw-mixed', mixed);
      wrapper.classList.toggle('pb-bmw-auto', auto);
      wrapper.classList.toggle('pb-bmw-template', template);
      wrapper.classList.toggle('pb-bmw-negative', negative);
      wrapper.classList.toggle('pb-bmw-zero', !mixed && !auto && this._isZero(this._values[field]));

      let display = '';
      if (mixed) display = 'Mixed';
      else if (auto) display = 'Auto';
      else display = this._formatForDisplay(this._values[field], { template });
      wrapper.textContent = display;
      wrapper.setAttribute('aria-valuetext', template ? `approximately ${display}` : display);
      const numeric = Number.parseFloat(String(this._values[field] || ''));
      if (!mixed && !auto && Number.isFinite(numeric)) {
        wrapper.setAttribute('aria-valuenow', String(numeric));
      } else {
        wrapper.removeAttribute('aria-valuenow');
      }

      const samples = this._diversity[field];
      if (mixed && Array.isArray(samples) && samples.length) {
        const tip =
          samples.slice(0, 3).join(' / ') + (samples.length > 3 ? ` +${samples.length - 3}` : '');
        wrapper.dataset.tooltip = `Mixed: ${tip}`;
      } else if (template) {
        wrapper.dataset.tooltip = `Template value · ${escapeHtml(this._values[field] || '')}`;
      } else if (auto) {
        wrapper.dataset.tooltip = 'Auto · click to override';
      } else {
        wrapper.dataset.tooltip = '';
      }
    }
    this._renderFocus();
  }

  _renderFocus() {
    for (const field of FIELDS) {
      const zone = this._sideEls.get(field);
      const label = this._labelEls.get(field);
      const isFocused = this._focusedField === field;
      if (zone) zone.classList.toggle('pb-bmw-zone-focus', isFocused);
      if (label) label.classList.toggle('pb-bmw-label-focus', isFocused);
    }
  }

  _isZero(value) {
    if (value == null || value === '') return true;
    const num = Number.parseFloat(String(value));
    return Number.isFinite(num) && Math.abs(num) < 0.001;
  }

  _formatForDisplay(value, { template } = {}) {
    if (value == null || value === '') return '0';
    const lower = String(value).trim().toLowerCase();
    if (lower === 'auto') return 'Auto';
    const num = Number.parseFloat(lower);
    if (!Number.isFinite(num)) return template ? `~${value}` : String(value);
    let px = num;
    if (lower.endsWith('rem') || lower.endsWith('em')) px = num * (this._rootPx || 16);
    if (this._unit === 'px') {
      const rounded = Math.round(px);
      return template ? `~${rounded}` : String(rounded);
    }
    const rem = px / (this._rootPx || 16);
    const rounded = Math.round(rem * 1000) / 1000;
    const text = Number.isInteger(rounded)
      ? String(rounded)
      : String(rounded).replace(/0+$/, '').replace(/\.$/, '');
    return template ? `~${text}` : text;
  }

  _openInputForField(field) {
    if (!FIELDS.includes(field)) return;
    if (this._activeInputEl) this._closeActiveInput({ commit: true });
    const wrapper = this._labelEls.get(field);
    if (!wrapper) return;
    this._editingField = field;
    wrapper.textContent = '';
    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'pb-bmw-input';
    input.inputMode = 'decimal';
    input.spellcheck = false;
    const value = this._values[field];
    input.value = this._mixed[field] ? '' : this._formatForDisplay(value);
    input.placeholder = this._mixed[field] ? 'Mixed' : '';
    wrapper.appendChild(input);
    input.focus();
    input.select();
    this._activeInputEl = input;
    this._activeInputCommit = (commit) => {
      const value = input.value;
      this._editingField = null;
      this._activeInputEl = null;
      this._activeInputCommit = null;
      this._renderLabels();
      if (commit) this._emit('edit-commit', { field, value });
    };
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this._closeActiveInput({ commit: true });
      } else if (e.key === 'Escape') {
        e.preventDefault();
        this._closeActiveInput({ commit: false });
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();
        this._emit('arrow-step', {
          field,
          direction: e.key === 'ArrowUp' ? 1 : -1,
          modifiers: { stepMode: e.shiftKey, fineMode: e.altKey }
        });
      }
    });
    input.addEventListener('blur', () => this._closeActiveInput({ commit: true }));
  }

  _closeActiveInput({ commit }) {
    if (!this._activeInputEl) return;
    const fn = this._activeInputCommit;
    if (typeof fn === 'function') fn(commit);
  }

  _onKeyDown(e) {
    const field = this._focusedField;
    if (!field) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this._openInputForField(field);
      return;
    }
    if (
      e.key === 'ArrowUp' ||
      e.key === 'ArrowDown' ||
      e.key === 'ArrowLeft' ||
      e.key === 'ArrowRight'
    ) {
      e.preventDefault();
      const direction = e.key === 'ArrowUp' || e.key === 'ArrowRight' ? 1 : -1;
      this._emit('arrow-step', {
        field,
        direction,
        modifiers: { stepMode: e.shiftKey, fineMode: e.altKey }
      });
    }
    if (e.key === 'Backspace' || e.key === 'Delete') {
      e.preventDefault();
      this._emit('reset-side', { field });
    }
  }
}

function chainSvg(filled = false) {
  // Compact chain icon. Filled variant (center "lock all") gets a fill color.
  if (filled) {
    return '<svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M6 8h4"/><path d="M5 5.5a2.5 2.5 0 0 0 0 5h.5"/><path d="M11 5.5a2.5 2.5 0 0 1 0 5h-.5"/></svg>';
  }
  return '<svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"><path d="M6.5 9.5L9.5 6.5"/><path d="M5.5 4.5a2 2 0 0 0 0 4l1-1"/><path d="M10.5 11.5a2 2 0 0 0 0-4l-1 1"/></svg>';
}
